import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {FORM_STATUSES} from '../../utils'
import { AxiosResponse } from 'axios'
import { UserDbModel, UserModel } from '../../auth/models/UserModel'
import { editUser, getUser } from '../../auth/redux/AuthCRUD'
import { OrganizationModel } from '../../auth/models/OrganizationModel'
import { viewOrganization } from '../../auth/redux/OrganizationCRUD'
import { Settings } from '../../../utils/config'
import { useSelector, shallowEqual } from 'react-redux'
import { RootState } from '../../../../setup'

const userSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  organizationId: Yup.number()
    .test("orgTest", "Please select an organization", function (value) {
      return this.parent.role == 'SUPERADMIN' || (value != null && value > 0);
    }),
  role: Yup.string()
    .required('Please select a role'),
  status: Yup.string().oneOf(['ACTIVE', 'INACTIVE']).required('Please select a status'),
});

const EditUser: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const {id}: any = useParams();  
  const [data, setData] = useState<UserDbModel>();
  const [orgData, setOrgData] = useState<Array<OrganizationModel>>();
  const [dataError, setDataError] = useState<DataError>();
  const user: UserModel = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel
  const isAdmin = user.gamyata && user.gamyata.role == 'ADMIN';
  const isSuperAdmin = user.gamyata && user.gamyata.role == 'SUPERADMIN';
  const initialValues = {name: '', email: '', role: isAdmin ? 'TESTER' : '', organizationId: -1, status: 'ACTIVE'};

  const formik = useFormik<any>({
    initialValues,
    validationSchema: userSchema,
    onSubmit: (values, {setStatus}) => {
      setLoading(true);
      const orgId = values.role == 'SUPERADMIN' ? -1 : parseInt(values.organizationId);
      const parichay = data?.parichay || {};
      parichay.fullName = values.name;
      parichay.givenName = values.name;
      editUser(id, parichay, values.email, values.role, orgId, values.status).then(({data: {id}}) => {
        setLoading(false);
        setStatus({state: FORM_STATUSES.SUCCESS, response: {id}});
      }).catch(() => {
        setLoading(false);
        setStatus({state: FORM_STATUSES.FAILURE, response: {error: 'Server Error!'}});
      });
    },
  });

  
  useEffect(() => {
    getUser(id).then((result: any) => {
      formik.setFieldValue('email', result.data.email);
      formik.setFieldValue('role', result.data.role);
      formik.setFieldValue('organizationId', result.data.organizationId);
      formik.setFieldValue('status', result.data.status);
      if (result.data.parichay) {
        formik.setFieldValue('name', result.data.parichay.fullName)
      }
      setData(result.data);
    }).catch(e => {
      console.error(e);
      setDataError({error: "Server Error! Cannot fetch user data!"})
    });
  }, []);

  useEffect(() => {
    viewOrganization().then((result) => setOrgData(result.data)).catch(e => setDataError({error: "Server Error! Cannot fetch org data!"}));
  }, []);

  if (dataError || data == null || orgData == null) {
    return (
      <div className='card mb-5 mb-xl-10'>
        <div
          className='card-header border-0 cursor-pointer'
          role='button'
          data-bs-toggle='collapse'
          data-bs-target='#kt_account_profile_details'
          aria-expanded='true'
          aria-controls='kt_account_profile_details'
        >
          {dataError ? (
            <div>
              <br />
                <div className='mb-lg-15 alert alert-danger d-flex flex-column-fluid flex-center'>
                  <div className='alert-text font-weight-bold'>{dataError.error}</div>
                </div>
                </div>
          ) : (
            <div>
              <br />
              <span>Loading... </span> <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
           </div>
        )}
        </div>
      </div>
      );
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div
        className='card-header border-0 cursor-pointer'
        role='button'
        data-bs-toggle='collapse'
        data-bs-target='#kt_edit_user'
        aria-expanded='true'
        aria-controls='kt_edit_user'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Edit User</h3>
        </div>
      </div>      

      <div id='kt_edit_user' className='collapse show'>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          <div className='card-body border-top p-9'>
          
          {formik.status && formik.status.state == FORM_STATUSES.SUCCESS ? (
              <div className='mb-lg-15 alert alert-success d-flex flex-column-fluid flex-center'>
                <div className='alert-text font-weight-bold'>Updated Successfully</div>
              </div>
            ) : null}

          {formik.status && formik.status.state == FORM_STATUSES.FAILURE ? (
            <div className='mb-lg-15 alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status.response?.error}</div>
            </div>
          ) : null}

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>Name</label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Name'
                  {...formik.getFieldProps('name')}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.name}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>Email</label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Email'
                  {...formik.getFieldProps('email')}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.email}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label htmlFor='s_role' className='col-lg-4 col-form-label required fw-bold fs-6'>Role</label>

              <div className='col-lg-8 fv-row'>
                <select
                  id='s_role' 
                  className='form-select form-select-solid form-select-lg'
                  {...formik.getFieldProps('role')}
                >
                  
                    <option value='TESTER'>{Settings.TesterLabel}</option>
                    <option value='ADMIN'>{Settings.AdminLabel}</option>
                    {isSuperAdmin ? 
                    <option value='SUPERADMIN'>Super Admin</option>
                    : null}
                  </select>
                {formik.touched.role && formik.errors.role && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.role}</div>
                  </div>
                )}
              </div>
            </div> 

            {isAdmin ? null : 
            <>
            <div className='row mb-6' style={{display: formik.values.role == 'SUPERADMIN' ? 'none' : 'flex'}}>
              <label htmlFor='s_org' className='col-lg-4 col-form-label required fw-bold fs-6'>Organization</label>

              <div className='col-lg-8 fv-row'>
                <select 
                  id='s_org'
                  className='form-select form-select-solid form-select-lg'
                  {...formik.getFieldProps('organizationId')}
                >
                  <option value="-1">Select Org</option>
                  {orgData?.map(org => (
                    <option value={org.id}>{org.name}</option>
                  ))}
                </select>
                {formik.touched.organizationId && formik.errors.organizationId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.organizationId}</div>
                  </div>
                )}
              </div>
            </div>
            </>
            }

            <div className='row mb-6'>
              <label htmlFor='s_status' className='col-lg-4 col-form-label required fw-bold fs-6'>Status</label>

              <div className='col-lg-8 fv-row'>
                <select 
                  id='s_status'
                  className='form-select form-select-solid form-select-lg'
                  {...formik.getFieldProps('status')}
                >
                  <option value="ACTIVE">Approved</option>
                  <option value="INACTIVE">Unapproved</option>
                </select>
              </div>
            </div>
          </div>

          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <button type='submit' className='btn btn-primary' disabled={loading}>
              {!loading && 'Save Changes'}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export {EditUser}
